import { createSlice } from '@reduxjs/toolkit';

/**
 * UI Slice — Global UI state management
 *
 * Manages:
 * - Sidebar open/collapsed state (desktop & mobile)
 * - Active modal tracking
 * - Theme (light/dark)
 * - Command palette state
 * - Global search state
 * - Notification badge count (placeholder)
 */
const initialState = {
  // Sidebar
  sidebarOpen: true,           // Mobile slide-out open
  sidebarCollapsed: false,     // Desktop collapsed to icons

  // Modals
  activeModal: null,           // { type: 'addTask', props: {} } or null

  // Theme
  theme: 'light',              // 'light' | 'dark'

  // Command palette (Cmd+K)
  commandPaletteOpen: false,

  // Mobile menu
  mobileMenuOpen: false,

  // Global search
  globalSearchQuery: '',
  globalSearchOpen: false,

  // Notifications badge (placeholder for module)
  unreadNotifications: 0,

  // Page-level loading overlay
  pageLoading: false,

  // Quick create modal
  quickCreateOpen: false,
  quickCreateType: null,       // 'task' | 'project' | 'note'
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // ── Sidebar ──
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    toggleSidebarCollapse: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },
    setSidebarCollapsed: (state, action) => {
      state.sidebarCollapsed = action.payload;
    },

    // ── Modals ──
    openModal: (state, action) => {
      state.activeModal = action.payload; // { type, props }
    },
    closeModal: (state) => {
      state.activeModal = null;
    },

    // ── Theme ──
    setTheme: (state, action) => {
      state.theme = action.payload;
      // Persist to localStorage
      try { localStorage.setItem('zentrix-theme', action.payload); } catch {}
    },
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
      try { localStorage.setItem('zentrix-theme', state.theme); } catch {}
    },

    // ── Command palette ──
    toggleCommandPalette: (state) => {
      state.commandPaletteOpen = !state.commandPaletteOpen;
    },
    setCommandPaletteOpen: (state, action) => {
      state.commandPaletteOpen = action.payload;
    },

    // ── Mobile menu ──
    toggleMobileMenu: (state) => {
      state.mobileMenuOpen = !state.mobileMenuOpen;
    },

    // ── Global search ──
    setGlobalSearchQuery: (state, action) => {
      state.globalSearchQuery = action.payload;
    },
    setGlobalSearchOpen: (state, action) => {
      state.globalSearchOpen = action.payload;
    },

    // ── Notifications ──
    setUnreadNotifications: (state, action) => {
      state.unreadNotifications = action.payload;
    },
    incrementUnread: (state) => {
      state.unreadNotifications += 1;
    },

    // ── Page loading ──
    setPageLoading: (state, action) => {
      state.pageLoading = action.payload;
    },

    // ── Quick create ──
    openQuickCreate: (state, action) => {
      state.quickCreateOpen = true;
      state.quickCreateType = action.payload || null;
    },
    closeQuickCreate: (state) => {
      state.quickCreateOpen = false;
      state.quickCreateType = null;
    },
  },
});

export const {
  toggleSidebar,
  toggleSidebarCollapse,
  setSidebarOpen,
  setSidebarCollapsed,
  openModal,
  closeModal,
  setTheme,
  toggleTheme,
  toggleCommandPalette,
  setCommandPaletteOpen,
  toggleMobileMenu,
  setGlobalSearchQuery,
  setGlobalSearchOpen,
  setUnreadNotifications,
  incrementUnread,
  setPageLoading,
  openQuickCreate,
  closeQuickCreate,
} = uiSlice.actions;

// ── Selectors ──
export const selectSidebarOpen = (state) => state.ui.sidebarOpen;
export const selectSidebarCollapsed = (state) => state.ui.sidebarCollapsed;
export const selectActiveModal = (state) => state.ui.activeModal;
export const selectTheme = (state) => state.ui.theme;
export const selectCommandPaletteOpen = (state) => state.ui.commandPaletteOpen;
export const selectGlobalSearchQuery = (state) => state.ui.globalSearchQuery;
export const selectGlobalSearchOpen = (state) => state.ui.globalSearchOpen;
export const selectUnreadNotifications = (state) => state.ui.unreadNotifications;
export const selectPageLoading = (state) => state.ui.pageLoading;
export const selectQuickCreateOpen = (state) => state.ui.quickCreateOpen;
export const selectQuickCreateType = (state) => state.ui.quickCreateType;

export default uiSlice.reducer;
